import type { IconButtonProps } from "@mui/material";
import { Box, Drawer, IconButton, Stack, ThemeProvider } from "@mui/material";
import { useState } from "react";
import { Link } from "react-router";
import useClientContext from "../../context/useClientContext";
import useUserContext from "../../context/useUserContext";
import { useVisualContext } from "../../context/VisualContext";
import useResolvedColorSchemeMode from "../../hooks/useResolvedColorSchemeMode";
import EntriliaLogo from "../../images/logo.png";
import EntriliaBetaLogo from "../../images/logobeta.png";
import { darkTheme, lightTheme } from "../../themes";
import { getLogoLinkClientCode } from "../../utilities/clientHelper";
import BottomMenu from "./BottomMenu";
import ClientSelector from "./client-selector/ClientSelector";
import DockPanelIcon from "./icons/DockPanelIcon";
import HidePanelIcon from "./icons/HidePanelIcon";
import ShowPanelIcon from "./icons/ShowPanelIcon";
import MainMenu from "./MainMenu";
import { MainMenuContextProvider } from "./MainMenuContextProvider";
import RestrictedEntityLevelAccessMessage from "./RestrictedEntityLevelAccessMessage";

const HideMenuIconButton = (props: IconButtonProps) => {
  const [hovered, setHovered] = useState(false);
  return (
    <IconButton onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} {...props}>
      {hovered ? <HidePanelIcon /> : <DockPanelIcon />}
    </IconButton>
  );
};

const ShowMenuIconButton = (props: IconButtonProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <IconButton onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} {...props}>
      {hovered ? <ShowPanelIcon /> : <DockPanelIcon />}
    </IconButton>
  );
};

const LeftSideRootMenu = () => {
  const { showSideMenu, toggleSideMenu } = useVisualContext();

  const { clients, isBetaAccessEnabled, isEntriliaUser } = useUserContext();
  const { clientCode, isAllowedToAccessAllEntities } = useClientContext();
  const resolvedMode = useResolvedColorSchemeMode();
  const clientSelectorTheme = resolvedMode === "dark" ? darkTheme : lightTheme;

  const logoLinkClientCode = getLogoLinkClientCode(isEntriliaUser, clients, clientCode);

  const menuWidth = "17rem";

  const logo = isBetaAccessEnabled ? EntriliaBetaLogo : EntriliaLogo;

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        id="left-side-root-menu"
        sx={{
          height: "100%",
          width: showSideMenu ? menuWidth : 0,
          transition: darkTheme.transitions.create("width", {
            easing: darkTheme.transitions.easing.sharp,
            duration: darkTheme.transitions.duration.leavingScreen,
          }),
          ...(showSideMenu && {
            transition: darkTheme.transitions.create("width", {
              easing: darkTheme.transitions.easing.easeOut,
              duration: darkTheme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        {!showSideMenu && (
          <ShowMenuIconButton
            sx={{ position: "absolute", top: darkTheme.spacing(2), zIndex: 1000 }}
            onClick={toggleSideMenu}
          />
        )}
        <Drawer
          sx={{
            colorScheme: "dark",
            flexShrink: 0,
            width: menuWidth,
            "& .MuiDrawer-paper": { padding: 0, width: menuWidth },
          }}
          variant="persistent"
          anchor="left"
          open={showSideMenu}
        >
          <Stack spacing={1} sx={{ height: "100%", display: "flex", py: 1 }}>
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", py: 0.5, px: 1.5 }}>
              <Link to={`/${logoLinkClientCode}/home`}>
                <img src={logo} alt="Entrilia logo" style={{ height: 20 }} />
              </Link>
              <HideMenuIconButton onClick={toggleSideMenu} />
            </Stack>
            <ThemeProvider theme={clientSelectorTheme}>
              <Box sx={{ px: 1 }}>
                <ClientSelector
                  selectHoveredBgColor={darkTheme.palette.action.hover}
                  selectBgColor={darkTheme.palette.action.selected}
                />
              </Box>
            </ThemeProvider>
            {!isAllowedToAccessAllEntities && (
              <Box sx={{ pl: 2 }}>
                <RestrictedEntityLevelAccessMessage />
              </Box>
            )}
            <MainMenuContextProvider>
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  borderBottom: "1px solid",
                  borderBottomColor: darkTheme.palette.divider,
                  borderTop: "1px solid",
                  borderTopColor: darkTheme.palette.divider,
                  px: 1,
                }}
              >
                <MainMenu />
              </Box>
              <Box sx={{ px: 1, "&&": { mt: 0 } }}>
                <BottomMenu />
              </Box>
            </MainMenuContextProvider>
          </Stack>
        </Drawer>
      </Box>
    </ThemeProvider>
  );
};

export default LeftSideRootMenu;
